// Administration → Connecteurs → « Application Google » (createDriveAppBlock; server/modules/drive.js,
// routes under /api/storage/google, administrators only): the office's ONE Google OAuth « Web
// application » client behind every « Google Drive » connector of the library. Not set: a numbered
// stepper, one short line per step, buttons opening the exact Google Cloud page, the addresses to
// paste (copy buttons) and the why in « ? » tooltips; the last step is the form (Client ID, secret,
// API key — the secret and the key sent once, never shown back: their last 4 characters; an empty
// field keeps them). The picker's project number is read from the Client ID by the server (no
// field). Set: a one-line summary with « Modifier » (the same stepper) and « Retirer » (every Google
// Drive connector disconnected: their access erased here, never revoked at Google); without the API
// key it is not ready (amber, « AJOUTER LA CLÉ API »). Below: « Vos Google Drive » (state, the folder
// each one is limited to with a lock, its account, the one storing the office's files; its next step
// as its button — SE CONNECTER, CHOISIR LE DOSSIER, RÉGLAGES — opens its window, myconnectors/
// drive.js, in place), « AJOUTER UN GOOGLE DRIVE » (the library's add window, then the new
// connector's connect window), and the server's disk. Hidden on a server without Google Drive (404).
// Mounted whatever the connector library: without it, a note.
import { h, button, input, field, badge, icon, infoTip } from '../../dom.js';
import { t } from '../../i18n/fr.js';
import { errorText } from './model.js';
import { alertBox, copyButton, formError, setBusy, focusKeeper } from './common.js';
import { childLayers } from '../automations/widgets.js';
import { LIBRARY_BASE, libraryAccess, normalizeLibrary } from '../myconnectors/model.js';
import { openDriveAdd, openDriveSettings } from '../myconnectors/drive.js';
import {
  DRIVE_BASE, GDRIVE_ID, driveAppOf, driveStatusOf, driveAppBadge, driveState, driveStateBadge, driveNeedsFolder, driveBytes, pendingText,
  originOf, hostOf, registrableDomain,
} from '../myconnectors/drive-model.js';
import '../myconnectors/strings.js';
import './strings.js';

export { DRIVE_BASE };
const NO_AUTOFILL = { autocomplete: 'off', autocapitalize: 'none', spellcheck: false, 'data-1p-ignore': '', 'data-lpignore': 'true', 'data-bwignore': '' };
/** The Google Cloud pages of each step (they open in the project selected there). */
export const GOOGLE_CLOUD = Object.freeze({
  project: 'https://console.cloud.google.com/projectcreate',
  driveApi: 'https://console.cloud.google.com/apis/library/drive.googleapis.com',
  pickerApi: 'https://console.cloud.google.com/apis/library/picker.googleapis.com',
  branding: 'https://console.cloud.google.com/auth/branding',
  audience: 'https://console.cloud.google.com/auth/audience',
  scopes: 'https://console.cloud.google.com/auth/scopes',
  clients: 'https://console.cloud.google.com/auth/clients',
  credentials: 'https://console.cloud.google.com/apis/credentials',
});
/** The project number opening a client id (« 860840548260-abc….apps.googleusercontent.com »), as the server reads it. */
const CLIENT_NUMBER_RE = /^(\d{5,20})-/;
/** The access « Limité à un dossier » asks Google for (declared in « Accès aux données »). */
export const DRIVE_FILE_SCOPE = 'https://www.googleapis.com/auth/drive.file';

/** « » kept with their words (no-break spaces): a line never ends on « nor starts with ». */
const nb = (text) => String(text).replace(/« /g, '«\u00a0').replace(/ »/g, '\u00a0»');

/** A « ? » saying `text`, named after what it explains. */
function tip(text, name) {
  return infoTip(nb(text), { label: t('adm.drive.tipAria', { name }) });
}

/** Replaces the children of `el` (nested arrays flattened, nothing for null). */
function fillBody(el, ...parts) {
  el.replaceChildren(...parts.flat(Infinity).filter(Boolean));
}

/** A Google Cloud page, in a new tab, as a small secondary button. */
function cloudLink(href, label) {
  return h('a', { class: 'btn btn-secondary btn-sm adm-drive-link', href, target: '_blank', rel: 'noopener noreferrer' },
    h('span', { class: 'btn-label' }, label), h('span', { class: 'sr-only' }, ` ${t('adm.acc.newTab')}`), icon('external', { size: 14 }));
}

/** An address to paste in Google Cloud: its small label, the address and its copy button. */
function pasteBox(ctx, get, key, label) {
  const code = h('code', { class: 'adm-cmd-code', dataset: { paste: key } });
  const el = h('div', { class: 'adm-drive-copy' },
    h('span', { class: 'adm-drive-copy-label' }, label),
    h('div', { class: 'adm-cmd' }, code, copyButton(() => get() || '', { toast: ctx.toast, variant: 'ghost' })));
  return { code, el };
}

/** A field of the form whose label has its « ? » (where to find the value), and a hint line set later. */
function tipField(label, control, help) {
  const wrap = field(label, control, { hint: ' ' });
  const lab = wrap.querySelector('.field-label');
  const row = h('div', { class: 'adm-drive-label' });
  wrap.insertBefore(row, lab);
  row.append(lab, tip(help, label));
  wrap.hint = wrap.querySelector('.field-hint');
  return wrap;
}

/** Sets a field's hint line (hidden when empty). */
function setHint(f, text) {
  f.hint.textContent = text || '';
  f.hint.hidden = !text;
}

/** A second action inside a step: one short line, its Google page, what to paste. */
function subStep(text, links = [], ...body) {
  return h('div', { class: 'adm-drive-substep' },
    h('p', { class: 'adm-drive-step-text is-body' }, nb(text)),
    links.length ? h('div', { class: 'adm-drive-step-actions' }, links) : null,
    body);
}

/** A short amber line of a step (what not to miss in Google Cloud). */
function warnLine(text) {
  return h('p', { class: 'adm-drive-step-warn' }, icon('alert', { size: 14 }), h('span', {}, nb(text)));
}

/**
 * One step of the stepper: its number, its title with a « ? », one short line, then its buttons,
 * addresses or form.
 * @param {number|string} n
 * @param {{title: string, tip?: string, text?: string|null, links?: HTMLElement[], body?: Array, last?: boolean}} step
 *   last: the form (its number stands out)
 */
function stepItem(n, { title, tip: help, text, links = [], body = [], last = false }) {
  return h('li', { class: ['adm-drive-step', { 'is-last': last }], dataset: { step: String(n) } },
    h('span', { class: 'adm-drive-step-num', 'aria-hidden': 'true' }, String(n)),
    h('div', { class: 'adm-drive-step-main' },
      h('div', { class: 'adm-drive-step-head' },
        h('h4', { class: 'adm-drive-step-title' }, title),
        help ? tip(help, title) : null),
      text ? h('p', { class: 'adm-drive-step-text' }, nb(text)) : null,
      links.length ? h('div', { class: 'adm-drive-step-actions' }, links) : null,
      body));
}

/** The server's disk: a line and a bar (used / total). */
function diskLine(disk) {
  if (!disk) return null;
  const pct = Math.max(0, Math.min(100, Math.round((disk.used / disk.total) * 100)));
  return h('div', { class: ['adm-drive-disk', { 'is-full': pct >= 85 }] },
    h('p', { class: 'adm-drive-disk-text' }, icon('monitor', { size: 16 }),
      h('span', {}, t('adm.drive.disk', { used: driveBytes(disk.used), total: driveBytes(disk.total), free: driveBytes(disk.free) }))),
    h('div', {
      class: 'adm-drive-bar', role: 'meter', 'aria-valuemin': '0', 'aria-valuemax': '100', 'aria-valuenow': String(pct),
      'aria-label': t('adm.drive.diskAria', { pct }),
    }, h('span', { class: 'adm-drive-bar-fill', style: { '--used': `${pct}%` } })));
}

/** « …ab12 »: the end of a client id, before Google's domain. */
function clientHint(clientId) {
  const s = String(clientId || '').replace(/\.apps\.googleusercontent\.com$/, '');
  return s ? `…${s.slice(-4)}` : '';
}

/**
 * The connectors whose access another client id (or « Retirer ») erases: connected or to reconnect.
 * @param {{connectors?: object[]}|null} status driveStatusOf()
 */
export function connectionCount(status) {
  return (status?.connectors || []).filter((c) => c.connected || c.needsReconnect).length;
}

/**
 * Creates the « Application Google » block.
 * @param {object} ctx app context
 * @param {{onChange?: (status: object) => void, navigate?: (url: string) => void, openWindow?: (url: string) => Window|null}} [opts]
 *   onChange: the status as read (driveStatusOf()); navigate / openWindow: passed to the connectors'
 *   windows (tests)
 * @returns {{el: HTMLElement, reload(): Promise<void>, destroy(): void}}
 */
export function createDriveAppBlock(ctx, { onChange = () => {}, navigate, openWindow } = {}) {
  let status = null;
  let destroyed = false;
  let busy = false;
  let loaded = false;
  let editing = false; // « Modifier »: the stepper of an app already set
  const keeper = focusKeeper();
  const windows = childLayers(null);
  const numberLine = h('p', { class: 'adm-drive-number' });
  const idInput = input({
    name: 'drive-client-id', maxLength: 200, placeholder: '123456789012-abc….apps.googleusercontent.com', ...NO_AUTOFILL,
    onInput: () => showNumber(),
  });
  const secretInput = input({ name: 'drive-client-secret', type: 'password', maxLength: 200, placeholder: 'GOCSPX-…', ...NO_AUTOFILL });
  const keyInput = input({ name: 'drive-api-key', type: 'password', maxLength: 100, placeholder: 'AIza…', ...NO_AUTOFILL });
  const fields = {
    clientId: tipField(t('adm.drive.clientId'), idInput, t('adm.drive.clientIdTip')),
    clientSecret: tipField(t('adm.drive.clientSecret'), secretInput, t('adm.drive.clientSecretTip')),
    apiKey: tipField(t('adm.drive.apiKey'), keyInput, t('adm.drive.apiKeyTip')),
  };
  const app = () => status?.app || driveAppOf(null);
  const home = pasteBox(ctx, () => app().homeUrl, 'home', t('adm.drive.home'));
  const privacy = pasteBox(ctx, () => app().privacyUrl, 'privacy', t('adm.drive.privacy'));
  const domain = pasteBox(ctx, () => authorisedDomain(), 'domain', t('adm.drive.domain'));
  const redirect = pasteBox(ctx, () => app().redirectUrl, 'redirect', t('adm.drive.redirect'));
  // The API key is restricted to the office's pages (the picker page sends its origin as Referer).
  const site = pasteBox(ctx, () => sitePattern(), 'site', t('adm.drive.site'));
  const scope = pasteBox(ctx, () => DRIVE_FILE_SCOPE, 'scope', t('adm.drive.scope'));
  const errorBox = formError();
  const save = button(t('adm.drive.save'), { variant: 'primary', icon: 'check', type: 'submit' });
  const cancel = button(t('common.cancel'), { variant: 'ghost', onClick: () => stopEditing() });
  const form = h('form', { class: 'adm-drive-form', novalidate: true, onSubmit: (e) => { e?.preventDefault?.(); send(); } },
    fields.clientId, numberLine, fields.clientSecret, fields.apiKey, errorBox,
    h('div', { class: 'adm-drive-foot' }, cancel, save));
  const state = h('span', { class: 'adm-drive-state' });
  const count = h('span', { class: 'adm-drive-count' });
  const connectorsEl = h('div', { class: 'adm-drive-conns-box' });
  const body = h('div', { class: 'adm-drive-body' }, h('p', { class: 'muted' }, t('adm.loading')));
  const el = h('details', { class: 'adm-drive' },
    h('summary', { class: 'adm-drive-summary' }, icon('plug', { size: 16 }), h('span', { class: 'adm-drive-title' }, t('adm.drive.title')), count, state,
      icon('chevronDown', { size: 16, class: 'adm-drive-chevron' })),
    body);

  /** « https://office…/* »: the API key's website restriction (the redirect address's origin). */
  function sitePattern() {
    const origin = originOf(app().redirectUrl);
    return origin ? `${origin}/*` : '';
  }

  /** The domain to declare in Google's Branding (the office's registrable domain). */
  function authorisedDomain() {
    return registrableDomain(hostOf(app().homeUrl || app().redirectUrl));
  }

  /** « Numéro du projet : … »: read from the Client ID typed (or the one saved by hand, older offices). */
  function showNumber() {
    const typed = String(idInput.value ?? '').trim();
    const a = app();
    let text = '';
    if (typed && typed === a.clientId && a.appId && !a.appIdFromClient) text = t('adm.drive.numberSaved', { n: a.appId });
    else {
      const m = CLIENT_NUMBER_RE.exec(typed);
      if (m) text = t('adm.drive.number', { n: m[1] });
    }
    numberLine.textContent = text;
    numberLine.hidden = !text;
  }

  function changed() {
    try { onChange(status); } catch (err) { console.error(err); }
  }

  /** Google sends the browser back to the office's public address: another one here loses the session. */
  function originWarning() {
    const there = originOf(app().redirectUrl);
    const here = String(globalThis.location?.origin || '');
    if (!there || !here || here === 'null' || here === there) return null;
    return alertBox('amber', t('adm.drive.otherOrigin', { there }), ' ', tip(t('adm.drive.otherOriginTip', { here }), t('adm.drive.title')));
  }

  /** A connector's settings window, in place (closed with the tab); what it changes is read again. */
  function openSettings(c) {
    if (destroyed) return null;
    const plugin = c.plugin || (c.mode ? { mode: c.mode } : undefined);
    const layer = windows.add(openDriveSettings(ctx, { id: c.id, label: c.label, catalogId: GDRIVE_ID, canManage: true, plugin }, {
      onChanged: () => { if (!destroyed) load({ refill: false }); },
      navigate, openWindow,
    }));
    layer?.onClose?.(() => { if (!destroyed) load({ refill: false }); });
    return layer;
  }

  /** « AJOUTER UN GOOGLE DRIVE »: the library's add window of the `gdrive` card, then the new one's connect window. */
  async function addConnector() {
    if (busy || destroyed) return;
    try {
      const data = normalizeLibrary(await ctx.api.get(LIBRARY_BASE));
      if (destroyed) return;
      const entry = data.catalogue.find((c) => c.id === GDRIVE_ID);
      if (!data.enabled || !entry) {
        ctx.toast.warn(data.message || t('adm.drive.libraryOff'));
        return;
      }
      if (!entry.available) {
        ctx.toast.warn(entry.unavailable || t('adm.drive.appFirst'));
        return;
      }
      windows.add(openDriveAdd(ctx, entry, {
        data, list: data.connectors, navigate,
        onAdded: (saved) => {
          if (destroyed) return;
          load({ refill: false });
          if (saved?.id) openSettings(saved);
        },
      }));
    } catch (err) {
      if (!destroyed) ctx.toast.error(errorText(err));
    }
  }

  /**
   * A Google Drive of the list: its name and state, what the office may see (a lock: its folder),
   * its account, and its next step as its button (SE CONNECTER → CHOISIR LE DOSSIER → RÉGLAGES).
   */
  function connectorRow(c) {
    const tag = driveStateBadge(c);
    const state = driveState(c);
    const needsFolder = driveNeedsFolder(c);
    const full = c.mode === 'full';
    const access = c.folder && (state === 'connected' || state === 'paused')
      ? h('span', { class: ['adm-drive-conn-access', { 'is-full': full }] }, icon(full ? 'folder' : 'lock', { size: 14 }),
        h('span', {}, t(full ? 'pc.textPlugin.gdrive.full' : 'pc.textPlugin.gdrive.limited', { folder: c.folder.name || c.folder.id })))
      : null;
    // What is still on the server: once it has its folder (it goes nowhere before).
    const rest = [c.account?.email || c.account?.name, c.storage && c.folder ? pendingText(c) : ''].filter(Boolean).join(' · ');
    const [key, variant, lead] = state === 'off' ? ['adm.drive.rowConnect', 'primary', 'key']
      : state === 'reconnect' ? ['adm.drive.rowReconnect', 'primary', 'key']
        : needsFolder ? ['adm.drive.rowPick', 'primary', 'folder'] : ['adm.drive.settings', 'ghost', null];
    return h('li', { class: ['adm-drive-conn', `is-${needsFolder ? 'needs-folder' : state}`, { 'is-storage': c.storage }], dataset: { id: c.id } },
      h('div', { class: 'adm-drive-conn-text' },
        h('span', { class: 'adm-drive-conn-name' }, c.label || c.id, badge(t(tag.key), tag.tone),
          c.storage ? badge(t('adm.drive.storageBadge'), 'info') : null),
        access || rest ? h('span', { class: 'adm-drive-conn-meta' }, access, access && rest ? ' · ' : null, rest || null) : null),
      keeper.reg(`drive-conn:${c.id}`, button(t(key), {
        variant, size: 'sm', icon: lead || undefined, iconRight: lead ? undefined : 'arrowRight',
        ariaLabel: lead ? t('pc.actionAria', { action: t(key), name: c.label || c.id }) : t('adm.drive.settingsAria', { name: c.label || c.id }),
        onClick: () => openSettings(c),
      })));
  }

  function renderConnectors() {
    if (!app().configured) {
      connectorsEl.replaceChildren();
      return;
    }
    const libraryOn = libraryAccess(ctx.store.get()).enabled;
    const list = status.connectors;
    const storage = list.find((c) => c.storage) || null;
    // The next step right after the app: add one (primary in the empty box; then beside the title).
    const add = (variant) => keeper.reg('drive-add', button(t('adm.drive.add'), { variant, size: 'sm', icon: 'plus', onClick: () => addConnector() }));
    fillBody(connectorsEl,
      h('div', { class: 'adm-drive-sub' },
        h('h4', { class: 'adm-drive-sub-title' }, t('adm.drive.connectors')),
        tip(t('adm.drive.connectorsTip'), t('adm.drive.connectors')),
        libraryOn && list.length ? h('span', { class: 'adm-drive-sub-add' }, add('secondary')) : null),
      list.length
        ? h('ul', { class: 'adm-drive-conns' }, list.map(connectorRow))
        : h('div', { class: 'adm-drive-empty' }, h('p', {}, t('adm.drive.noConnectors')), libraryOn ? add('primary') : null),
      // Where the office's files are while no Google Drive says it (its FICHIERS DU BUREAU badge).
      list.length && !storage
        ? h('p', { class: 'adm-drive-storage is-none' }, icon('info', { size: 14 }), h('span', {}, nb(t('adm.drive.storageNone'))), ' ',
          tip(t('adm.drive.storageTip'), t('adm.drive.storageName')))
        : null,
      libraryOn ? null : alertBox('amber', t('adm.drive.libraryOff')));
  }

  /**
   * The app set: one line (its codes' last characters) with « Modifier » and « Retirer ». Without
   * the API key it is not ready (« Limité à un dossier » cannot choose its folder): amber, and
   * « AJOUTER LA CLÉ API » opens the form on that field.
   */
  function readyCard(a) {
    const facts = [
      t('adm.drive.readyClient', { hint: clientHint(a.clientId) }),
      a.apiKeyHint ? t('adm.drive.readyKey', { hint: a.apiKeyHint }) : null,
      a.appId ? t('adm.drive.readyNumber', { n: a.appId }) : null,
    ].filter(Boolean);
    const noKey = !a.pickerReady;
    return h('div', { class: ['adm-drive-ready', { 'is-warn': noKey }] },
      h('div', { class: 'adm-drive-ready-text' },
        h('p', { class: 'adm-drive-ready-title' }, h('span', { class: 'adm-drive-ready-icon' }, icon(noKey ? 'alert' : 'check', { size: 14 })),
          t(noKey ? 'adm.drive.noKeyTitle' : 'adm.drive.readyTitle')),
        noKey ? h('p', { class: 'adm-drive-ready-warn' }, nb(t('adm.drive.noKeyText'))) : null,
        h('p', { class: 'adm-drive-ready-facts' }, facts.join(' · '))),
      h('div', { class: 'adm-drive-ready-actions' },
        noKey && !editing
          ? keeper.reg('drive-addkey', button(t('adm.drive.addKey'), { variant: 'primary', size: 'sm', icon: 'key', onClick: () => startEditing(keyInput) }))
          : null,
        editing ? null : keeper.reg('drive-edit', button(t('adm.drive.edit'), { variant: 'secondary', size: 'sm', icon: 'pencil', onClick: () => startEditing() })),
        keeper.reg('drive-clear', button(t('adm.drive.clear'), { variant: 'ghost', size: 'sm', class: 'adm-drive-clear', onClick: () => remove() }))));
  }

  /** The numbered steps in Google Cloud, then the form. */
  function stepper() {
    const dom = authorisedDomain();
    return h('ol', { class: 'adm-drive-steps' },
      stepItem(1, {
        title: t('adm.drive.step1'), tip: t('adm.drive.step1Tip'), text: t('adm.drive.step1Text'),
        links: [cloudLink(GOOGLE_CLOUD.project, t('adm.drive.step1Link'))],
      }),
      stepItem(2, {
        title: t('adm.drive.step2'), tip: t('adm.drive.step2Tip'),
        links: [cloudLink(GOOGLE_CLOUD.driveApi, t('adm.drive.step2Drive')), cloudLink(GOOGLE_CLOUD.pickerApi, t('adm.drive.step2Picker'))],
      }),
      stepItem(3, {
        title: t('adm.drive.step3'), tip: t('adm.drive.step3Tip'), text: t('adm.drive.step3Text'),
        links: [cloudLink(GOOGLE_CLOUD.branding, t('adm.drive.step3Link'))],
        body: [h('p', { class: 'adm-drive-step-text is-body' }, nb(t('adm.drive.step3Paste'))),
          home.el, privacy.el, dom ? domain.el : warnLine(t('adm.drive.domainNone'))],
      }),
      stepItem(4, {
        title: t('adm.drive.step4'), tip: t('adm.drive.step4Tip'), text: t('adm.drive.step4Text'),
        links: [cloudLink(GOOGLE_CLOUD.audience, t('adm.drive.step4Audience'))],
        body: [subStep(t('adm.drive.step4Scope'), [cloudLink(GOOGLE_CLOUD.scopes, t('adm.drive.step4Scopes'))], scope.el)],
      }),
      stepItem(5, {
        title: t('adm.drive.step5'), tip: t('adm.drive.step5Tip'), text: t('adm.drive.step5Text'),
        links: [cloudLink(GOOGLE_CLOUD.clients, t('adm.drive.step5Link'))],
        // Google shows a new client's secret once, in its « client created » window.
        body: [redirect.el, warnLine(t('adm.drive.step5Agent')), warnLine(t('adm.drive.step5Secret'))],
      }),
      stepItem(6, {
        title: t('adm.drive.step6'), tip: t('adm.drive.step6Tip'), text: t('adm.drive.step6Text'),
        links: [cloudLink(GOOGLE_CLOUD.credentials, t('adm.drive.step6Link'))],
        body: [site.el],
      }),
      stepItem(7, { title: t('adm.drive.step7'), tip: t('adm.drive.step7Tip'), body: [form], last: true }));
  }

  function render() {
    const restore = keeper.begin();
    const a = app();
    const tag = driveAppBadge(a);
    state.replaceChildren(badge(t(tag.key), tag.tone));
    // « 3 Google Drive · 1 connecté »: nothing while there is none.
    const total = a.configured && status ? status.connectors.length : 0;
    const on = total ? status.connectors.filter((c) => c.connected).length : 0;
    const more = on === total ? '' : ` · ${on ? t('adm.drive.countOn', { n: on }) : t('adm.drive.countNone')}`;
    count.replaceChildren(total ? badge(`${t('adm.drive.count', { n: total })}${more}`, 'neutral') : '');
    home.code.textContent = a.homeUrl || '';
    privacy.code.textContent = a.privacyUrl || '';
    domain.code.textContent = authorisedDomain();
    redirect.code.textContent = a.redirectUrl || '';
    site.code.textContent = sitePattern();
    scope.code.textContent = DRIVE_FILE_SCOPE;
    setHint(fields.clientSecret, a.secretHint ? t('adm.drive.secretKeep', { hint: a.secretHint }) : '');
    setHint(fields.apiKey, a.apiKeyHint ? t('adm.drive.apiKeyKeep', { hint: a.apiKeyHint }) : '');
    setHint(fields.clientId, '');
    showNumber();
    if (!a.configured) editing = false;
    cancel.hidden = !a.configured;
    renderConnectors();
    const setup = !a.configured || editing;
    fillBody(body,
      setup && !a.configured
        ? h('p', { class: 'adm-drive-lead' }, t('adm.drive.lead'), ' ', tip(t('adm.drive.leadTip'), t('adm.drive.title')))
        : null,
      originWarning(),
      a.configured ? readyCard(a) : null,
      setup ? stepper() : null,
      connectorsEl,
      // The server's disk once the app is set (noise during the setup).
      a.configured ? diskLine(status?.disk) : null);
    restore();
  }

  /** « Modifier » (or « AJOUTER LA CLÉ API »: on that field). */
  function startEditing(focus = idInput) {
    editing = true;
    render();
    focus.focus?.();
    focus.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
  }

  function stopEditing() {
    editing = false;
    fill();
    for (const f of Object.values(fields)) f.setError(null);
    errorBox.set('');
    render();
  }

  function fill() {
    idInput.value = status?.app.clientId || '';
    secretInput.value = '';
    keyInput.value = '';
  }

  /** Reads the status; `refill` false (a quiet reload) keeps what is typed in the form. */
  async function load({ refill = true } = {}) {
    try {
      const res = driveStatusOf(await ctx.api.get(`${DRIVE_BASE}/status`));
      if (destroyed) return;
      status = res;
      el.hidden = false;
      if (refill || !loaded) fill();
      render();
      // Opened by itself the first time while the office's Google app is not ready (or lacks its key).
      if (!loaded && !status.app.pickerReady) el.open = true;
      loaded = true;
      changed();
    } catch (err) {
      if (destroyed) return;
      // A server without Google Drive: no block.
      if (err?.status === 404) {
        el.hidden = true;
        return;
      }
      if (!status) body.replaceChildren(alertBox('danger', errorText(err)));
    }
  }

  async function send() {
    if (busy) return;
    for (const f of Object.values(fields)) f.setError(null);
    errorBox.set('');
    const clientId = idInput.value.trim();
    busy = true;
    setBusy(save, true);
    try {
      // Another client: every Google Drive connector's access is erased by the server (said first).
      const n = connectionCount(status);
      if (status?.app.clientId && clientId && clientId !== status.app.clientId && n) {
        const ok = await ctx.overlay.confirm({
          title: t('adm.drive.changeTitle'), message: t('adm.drive.changeText', { n }), confirmLabel: t('adm.drive.changeOk'), danger: true,
        });
        if (!ok || destroyed) return;
      }
      // A well-formed client without any API key: saved only once said (no folder can be chosen).
      if (CLIENT_NUMBER_RE.test(clientId) && !keyInput.value.trim() && !status?.app.apiKeyHint) {
        const ok = await ctx.overlay.confirm({
          title: t('adm.drive.noKeyConfirmTitle'), message: t('adm.drive.noKeyConfirmText'), confirmLabel: t('adm.drive.noKeyConfirmOk'),
        });
        if (destroyed) return;
        if (!ok) {
          keyInput.focus?.();
          return;
        }
      }
      const next = driveAppOf(await ctx.api.put(`${DRIVE_BASE}/app`, {
        clientId, clientSecret: secretInput.value.trim(), apiKey: keyInput.value.trim(),
      }));
      if (destroyed) return;
      status = { ...(status || { storage: null, connectors: [], disk: null }), app: next };
      editing = false;
      fill();
      render();
      ctx.toast.success(t('adm.drive.saved'));
      if (next.connectorsCleared) ctx.toast.warn(t('adm.drive.connectorsCleared', { n: next.connectorsCleared }));
      await load();
    } catch (err) {
      secretInput.value = '';
      keyInput.value = '';
      if (destroyed) return;
      const f = err?.details?.field;
      if (f && fields[f]) fields[f].setError(errorText(err));
      else errorBox.set(errorText(err));
    } finally {
      busy = false;
      setBusy(save, false);
    }
  }

  async function remove() {
    if (busy) return;
    const ok = await ctx.overlay.confirm({ title: t('adm.drive.clearTitle'), message: t('adm.drive.clearText'), confirmLabel: t('adm.drive.clear'), danger: true });
    if (!ok || destroyed) return;
    busy = true;
    try {
      const next = driveAppOf(await ctx.api.del(`${DRIVE_BASE}/app`));
      if (destroyed) return;
      ctx.toast.success(t('adm.drive.cleared'));
      if (next.connectorsCleared) ctx.toast.warn(t('adm.drive.connectorsDisconnected', { n: next.connectorsCleared }));
      await load();
    } catch (err) {
      if (!destroyed) ctx.toast.error(errorText(err));
    } finally {
      busy = false;
    }
  }

  load();
  return {
    el,
    reload: () => load({ refill: false }),
    destroy() {
      destroyed = true;
      secretInput.value = '';
      keyInput.value = '';
      windows.closeAll('route');
    },
  };
}
